class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class MinoCartDiscount extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', (event) => event.stopPropagation());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('input')) {
        event.preventDefault();
        event.stopPropagation();
        this.updateDiscount();
      }
    });
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-apply-discount')) this.updateDiscount();
      if (button.hasAttribute('data-remove-discount')) this.updateDiscount(button.dataset.removeDiscount);
    });
  }

  connectedCallback() {
    this.readCart().then((cart) => {
      if (this.isConnected && !this.busy) this.showCodes(cart);
    }).catch(() => {});
  }

  async readCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }

  message(key, error = false) {
    const status = this.querySelector('.mino-promo-status');
    status.textContent = this.dataset[key];
    status.classList.toggle('is-error', error);
    this.querySelector('input').setAttribute('aria-invalid', String(error));
  }

  showCodes(cart) {
    const list = this.querySelector('.mino-promo-codes');
    list.replaceChildren();
    (cart.discount_codes || []).filter((entry) => entry.applicable).forEach((entry) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mino-promo-code';
      button.dataset.removeDiscount = entry.code;
      button.textContent = entry.code + ' ×';
      button.setAttribute('aria-label', this.dataset.remove + ' ' + entry.code);
      list.append(button);
    });
  }

  async updateDiscount(removeCode) {
    if (this.busy) return;
    const input = this.querySelector('input');
    const code = input.value.trim();
    if (!removeCode && !code) {
      this.message('empty', true);
      input.focus();
      return;
    }
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    this.querySelectorAll('button,input').forEach((element) => element.disabled = true);
    this.message('loading');
    try {
      const previous = await this.readCart();
      const codes = (previous.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);
      const nextCodes = removeCode
        ? codes.filter((entry) => entry.toLowerCase() !== removeCode.toLowerCase())
        : [...new Set([...codes, code].map((entry) => entry.toUpperCase()))];
      const drawer = this.closest('cart-drawer');
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: nextCodes.join(','),
          sections: drawer.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname
        })
      });
      const cart = await response.json();
      if (!response.ok || cart.errors) throw new Error('Discount update failed');
      const accepted = (cart.discount_codes || []).some((entry) => entry.code.toLowerCase() === code.toLowerCase() && entry.applicable);
      if (!cart.sections || drawer.getSectionsToRender().some((section) => !cart.sections[section.id])) {
        window.location.reload();
        return;
      }
      drawer.renderContents(cart);
      const updated = drawer.querySelector('mino-cart-discount');
      updated.showCodes(cart);
      updated.querySelector('input').value = accepted || removeCode ? '' : code;
      updated.message(removeCode ? 'removed' : accepted ? 'applied' : 'invalid', !removeCode && !accepted);
      if (drawer.classList.contains('active')) trapFocus(drawer.querySelector('#CartDrawer'), updated.querySelector('input'));
    } catch (error) {
      if (this.isConnected) this.message('error', true);
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.querySelectorAll('button,input').forEach((element) => element.disabled = false);
    }
  }
}
if (!customElements.get('mino-cart-discount')) customElements.define('mino-cart-discount', MinoCartDiscount);
